import { ref } from "vue";

import { $streemApiV1 } from "shared/boot/api";
import { getLocaleText } from "shared/boot/i18n";
import {
  FACEBOOK_SDK_CONFIG,
  FACEBOOK_SDK_URL,
  FB_PERMISSIONS,
  SocialPlatform,
} from "shared/constants";
import {
  FacebookSocialIntegration,
  InstagramSocialIntegration,
} from "shared/resources";
import loadScript from "shared/services/script-loader";

import type {
  PermissionList,
  SetGrantedData,
  SocialInteractionPage,
  SocialUserRecord,
  StatusResponse,
  UseMetaAuth,
} from "./types";

const TIMEOUT_IN_MILLISECONDS = 10000;

const authErrors = ({ status }: StatusResponse) => {
  if (status === "unknown") return null;

  if (status === "network") {
    return new Error(getLocaleText("helpers.errors.connection_error_message"));
  }

  if (status === "rateLimit") {
    return new Error(
      getLocaleText(
        "social_engage_add_facebook_or_instagram_account.error_rate_limit"
      )
    );
  }

  return new Error(
    getLocaleText(
      "social_engage_add_facebook_or_instagram_account.error_authenticating_facebook"
    )
  );
};

// eslint-disable-next-line import/prefer-default-export
export function useMetaAuth({
  platform,
  onStatusChange = (): void => {},
}: UseMetaAuth) {
  const grantedPermissions = ref<PermissionList[]>([]);
  const isFacebookReady = ref(false);
  const isLoggedIn = ref(false);
  const isProcessing = ref(false);
  const userExitedLoginProcess = ref(false);
  const abortController = ref(new AbortController());

  const setGrantedPermissions = () =>
    new Promise<void>((resolve) => {
      window.FB.api<{ data: SetGrantedData[] }>(
        "/me/permissions",
        (response) => {
          response.data.forEach((data) => {
            if (data.status === "granted") {
              grantedPermissions.value.push(data.permission);
            }
          });

          resolve();
        }
      );
    });

  const getFacebookPages = async (
    accessToken: string,
    userID: string
  ): Promise<SocialUserRecord[]> => {
    const params = { accessToken, userID };

    const pages = (
      await $streemApiV1.post("social_integrations/facebook/pages", {
        transformKeys: true,
        params,
        signal: abortController.value.signal,
      })
    ).data;

    return pages.reduce(
      (acc: Partial<SocialUserRecord>[], page: SocialInteractionPage) => {
        if (platform === SocialPlatform.FACEBOOK) {
          acc.push({
            accessToken: page.accessToken,
            id: page.id,
            metadata: {
              profilePhotoUrl: page.profilePhoto?.url,
            },
            name: page.name,
            permissions: grantedPermissions.value.join(","),
            selected: true,
            type: "facebook",
          });
        }

        if (
          platform === SocialPlatform.INSTAGRAM &&
          page.connectedInstagramAccount
        ) {
          acc.push({
            accessToken: page.accessToken,
            id: page.connectedInstagramAccount.id,
            metadata: {
              profilePhotoUrl: page.connectedInstagramAccount.profilePictureUrl,
              username: page.connectedInstagramAccount.username,
            },
            name: page.connectedInstagramAccount.name,
            permissions: grantedPermissions.value.join(","),
            selected: true,
            type: "instagram",
          });
        }

        return acc;
      },
      []
    );
  };

  const loginWithFacebook = (): Promise<SocialUserRecord[]> => {
    isLoggedIn.value = false;
    userExitedLoginProcess.value = false;

    return new Promise((resolve, reject) => {
      try {
        // this has to be synchronous
        window.FB.login(
          (response) => {
            if (response.status === "connected") {
              const { accessToken, userID } = response.authResponse;

              if (accessToken && userID) {
                setGrantedPermissions()
                  .then(() => getFacebookPages(accessToken, userID))
                  .then((pages) => {
                    isLoggedIn.value = pages?.length > 0;
                    isProcessing.value = false;
                    resolve(pages);
                  })
                  .catch(() => {
                    isProcessing.value = false;
                    reject();
                  });

                return;
              }
            }

            if (response.status === "unknown") {
              userExitedLoginProcess.value = true;
            }

            isProcessing.value = false;
            reject(authErrors(response));
          },
          {
            auth_type: "rerequest",
            scope: FB_PERMISSIONS.join(","),
          }
        );
      } catch {
        abortController.value.abort();
        // could be anything - but during testing it was rate limited.
        reject(authErrors({ status: "rateLimit" }));
        isProcessing.value = false;
      }
    });
  };

  const mountFacebookSDK = () => {
    const sdkPromise = new Promise<void>((resolve, reject) => {
      try {
        if (!window.FB) {
          window.fbAsyncInit = () => {
            window.FB.Event.subscribe(
              "auth.statusChange",
              (status: facebook.StatusResponse) => onStatusChange(status)
            );

            window.FB.init(FACEBOOK_SDK_CONFIG);
            isFacebookReady.value = true;
            resolve();
          };

          loadScript(FACEBOOK_SDK_URL);
        } else {
          isFacebookReady.value = true;
          resolve();
        }
      } catch {
        isProcessing.value = false;
        reject(authErrors({ status: "network" }));
      }
    });

    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => {
        isProcessing.value = false;
        reject(authErrors({ status: "network" }));
      }, TIMEOUT_IN_MILLISECONDS);
    });

    return Promise.race([sdkPromise, timeoutPromise]);
  };

  const login = async (): Promise<SocialUserRecord[]> => {
    isProcessing.value = true;
    await mountFacebookSDK();

    return loginWithFacebook();
  };

  return {
    Integration:
      platform === "Facebook"
        ? FacebookSocialIntegration
        : InstagramSocialIntegration,
    abortController,
    isLoggedIn,
    isProcessing,
    isScriptLoaded: isFacebookReady,
    userExitedLoginProcess,

    login,
    mountFacebookSDK,
  };
}
